import { type ReactNode } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Check,
  CircleDot,
  Clock,
  FileText,
  MinusCircle,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { cn } from '@/nucleo/utilitarios';
import { STATUS_TONE, type StatusTone } from '@/nucleo/statusBadgeTokens';
import { Badge } from '@/ui/base/badge';
import { Skeleton } from '@/ui/base/skeleton';

export type StatusDoPasso = 'completed' | 'current' | 'pending' | 'error' | 'warning' | 'skipped';

export interface DocumentoDoPasso {
  label: string;
  href?: string;
}

export interface PendenciaDoPasso {
  label: string;
  severidade?: 'alta' | 'media' | 'baixa';
}

export interface PassoDeProgresso {
  /** Rótulo curto da etapa. */
  label: string;
  /** Status visual da etapa. Só um passo deveria ser `current` por vez. */
  status: StatusDoPasso;
  /** Data ISO da movimentação. */
  date?: string | null;
  /** Descrição complementar (1 linha curta). */
  description?: string;
  /** Origem da ação (ex.: "Fiscalização", "DTP", "Financeiro"). */
  origem?: string;
  /** Documento relacionado, com link opcional. */
  documento?: DocumentoDoPasso;
  /** Pendência associada à etapa (selo curto, ex.: "Prazo de resposta vencido"). */
  pendencia?: PendenciaDoPasso;
  /** Próxima ação esperada — só aparece no passo `current`. */
  proximaAcao?: string;
}

const TOM_DA_SEVERIDADE: Record<NonNullable<PendenciaDoPasso['severidade']>, StatusTone> = {
  alta: 'destructive',
  media: 'warning',
  baixa: 'neutral',
};

export interface ProgressTimelineProps {
  passos: PassoDeProgresso[];
  /** Estado de carregamento: mostra esqueleto de 3 etapas. */
  carregando?: boolean;
  /** Mensagem quando não há passos (padrão: nada renderiza). */
  mensagemVazia?: ReactNode;
  /**
   * `progresso` (padrão) destaca o passo atual com anel e fundo. `simples`
   * desenha o passo atual só pelo ícone, como a `SolicitacaoTimeline` do
   * protótipo — é a que o acompanhamento de inscrição e de solicitação usa lá.
   */
  variante?: 'progresso' | 'simples';
  className?: string;
}

const CONFIGURACAO_POR_STATUS: Record<
  StatusDoPasso,
  { icone: typeof Check; cor: string; fundo: string; linha: string; anel?: string }
> = {
  completed: { icone: Check, cor: 'text-success', fundo: 'bg-success/15', linha: 'bg-success/40' },
  current: {
    icone: Clock,
    cor: 'text-primary',
    fundo: 'bg-primary/15',
    linha: 'bg-border',
    anel: 'ring-2 ring-primary/40 ring-offset-2 ring-offset-background',
  },
  pending: { icone: CircleDot, cor: 'text-muted-foreground', fundo: 'bg-muted', linha: 'bg-border' },
  error: { icone: X, cor: 'text-destructive', fundo: 'bg-destructive/15', linha: 'bg-border' },
  warning: { icone: AlertTriangle, cor: 'text-warning', fundo: 'bg-warning/15', linha: 'bg-border' },
  skipped: {
    icone: MinusCircle,
    cor: 'text-muted-foreground',
    fundo: 'bg-muted/50',
    linha: 'bg-border border-dashed',
  },
};

function formatarData(data: string | null | undefined): string | null {
  if (!data) return null;
  const instante = new Date(data);
  if (Number.isNaN(instante.getTime())) return null;
  return format(instante, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

/**
 * Linha do tempo de PROGRESSO — marcos planejados de um processo (5-7
 * etapas: lavrada → notificada → em análise → aplicada → cobrança), não
 * eventos já ocorridos (isso é `HistoryTimeline.tsx`). Só um passo deveria
 * estar `current` por vez. Cópia estrutural do protótipo
 * (`timeline/ProgressTimeline.tsx`), promovida a componente do design
 * system porque mais de uma tela vai precisar da mesma peça visual.
 *
 * Sem `responsavel`, `motivo`, `pendencia`, `slaLabel`, `auditoriaRef` do
 * protótipo — a Multa (primeiro uso) não precisa deles. Adicionar quando
 * um consumidor de verdade precisar, não antes.
 */
export function ProgressTimeline({
  passos,
  carregando = false,
  mensagemVazia,
  variante = 'progresso',
  className,
}: ProgressTimelineProps) {
  if (carregando) {
    return (
      <div className={cn('space-y-4', className)} aria-busy="true">
        {[0, 1, 2].map((indice) => (
          <div key={indice} className="flex gap-3">
            <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            <div className="flex-1 space-y-1.5 pt-1">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (passos.length === 0) {
    if (!mensagemVazia) return null;
    return <p className={cn('text-sm text-muted-foreground', className)}>{mensagemVazia}</p>;
  }

  return (
    <ol className={cn('relative space-y-0', className)}>
      {passos.map((passo, indice) => {
        const config = CONFIGURACAO_POR_STATUS[passo.status];
        const Icone = config.icone;
        const ultimo = indice === passos.length - 1;
        const dataFormatada = formatarData(passo.date);
        const atual = passo.status === 'current';
        const destacarAtual = atual && variante === 'progresso';
        const pendente = passo.status === 'pending' || passo.status === 'skipped';

        return (
          <li key={indice} className="relative flex gap-3 pb-6 last:pb-0">
            {!ultimo && (
              <div
                className={cn('absolute bottom-0 left-[15px] top-8 w-0.5', config.linha)}
                aria-hidden="true"
              />
            )}

            <div
              className={cn(
                'relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                config.fundo,
                destacarAtual && config.anel,
              )}
            >
              <Icone className={cn('h-4 w-4', config.cor)} aria-hidden="true" />
            </div>

            <div
              className={cn(
                'min-w-0 flex-1 pt-0.5',
                // bg-primary/5 (classe do protótipo) fica praticamente
                // invisível no tema escuro — --card e --primary têm luminosidade
                // próxima o bastante pra a mistura de 5% não aparecer. Testado
                // ao vivo nos dois temas: /10 é o menor valor que reproduz o
                // destaque visível do print do protótipo em ambos.
                destacarAtual && '-mx-1 -my-1 rounded-lg bg-primary/10 px-3 py-2',
              )}
            >
              <div className="flex flex-wrap items-center gap-2">
                <p
                  className={cn(
                    'wrap-break-word text-sm font-medium',
                    pendente && 'text-muted-foreground',
                    atual && 'text-foreground',
                  )}
                >
                  {passo.label}
                </p>
                {passo.origem && (
                  <Badge variant="outline" className="h-4 py-0 px-1.5 text-[10px] font-normal">
                    {passo.origem}
                  </Badge>
                )}
              </div>

              {dataFormatada && <p className="mt-0.5 text-xs text-muted-foreground">{dataFormatada}</p>}

              {passo.description && (
                <p className="mt-1 text-xs text-muted-foreground">{passo.description}</p>
              )}

              {passo.documento && (
                <div className="mt-1.5">
                  {passo.documento.href ? (
                    <a
                      href={passo.documento.href}
                      className="inline-flex items-center gap-1 text-xs text-link hover:underline"
                    >
                      <FileText className="h-3 w-3" aria-hidden />
                      {passo.documento.label}
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <FileText className="h-3 w-3" aria-hidden />
                      {passo.documento.label}
                    </span>
                  )}
                </div>
              )}

              {passo.pendencia && (
                <div className="mt-1.5">
                  <Badge
                    variant="outline"
                    className={cn(
                      'gap-1 text-[10px] font-normal',
                      STATUS_TONE[TOM_DA_SEVERIDADE[passo.pendencia.severidade ?? 'media']],
                    )}
                  >
                    <AlertCircle className="h-3 w-3" aria-hidden />
                    {passo.pendencia.label}
                  </Badge>
                </div>
              )}

              {atual && passo.proximaAcao && (
                // text-link, não text-primary: mesmo achado recorrente de
                // PerfilBadge.tsx e RecursoBadges.tsx — texto puro (sem
                // preenchimento) em --primary não bate contraste AA no tema
                // escuro (tokens.css, comentário de --link).
                <div className="mt-2 flex items-start gap-1.5 text-xs text-link">
                  <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="wrap-break-word">
                    <span className="font-semibold">Próximo passo: </span>
                    {passo.proximaAcao}
                  </span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default ProgressTimeline;
